// src/pages/ChecklistTemplateManager.jsx

import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import {
  collection,
  query,
  orderBy,
  getDocs,
  addDoc,
  updateDoc,
  doc,
  serverTimestamp,
  writeBatch
} from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import {
  FaClipboardList,
  FaPlus,
  FaEdit,
  FaToggleOn,
  FaToggleOff,
  FaArrowUp,
  FaArrowDown,
  FaSave,
  FaTimes,
  FaSpinner,
  FaInfoCircle
} from 'react-icons/fa';
import toast from 'react-hot-toast';
import styles from './ChecklistTemplateManager.module.css';

const DEFAULT_TEMPLATES = [
  { title: 'Offer Letter Signed Back',      description: 'Employee has signed and returned the offer letter.',           category: 'documentation', order: 1 },
  { title: 'Digital ID Card Issued',        description: 'Digital ID card generated and shared with employee.',          category: 'onboarding',    order: 2 },
  { title: 'Hard Copy ID Card Issued',      description: 'Physical ID card printed and handed over.',                    category: 'onboarding',    order: 3 },
  { title: 'Accommodations Arranged',       description: 'Accommodation/seating/equipment arranged for the employee.',   category: 'onboarding',    order: 4 },
  { title: 'HR Manual Sent',                description: 'HR policy manual shared with the employee.',                   category: 'documentation', order: 5 },
  { title: 'Employee Manual Sent',          description: 'Employee handbook / manual shared with the employee.',         category: 'documentation', order: 6 },
  { title: 'System Access Granted',         description: 'Login credentials for required tools/systems provided.',       category: 'onboarding',    order: 7 },
  { title: 'Bank Details Collected',        description: 'Bank account details collected for salary processing.',        category: 'documentation', order: 8 },
  { title: 'Emergency Contact Collected',   description: 'Emergency contact information obtained from employee.',        category: 'documentation', order: 9 },
  { title: 'Induction / Orientation Done',  description: 'Initial induction session completed with the employee.',      category: 'onboarding',    order: 10 },
];

const CATEGORIES = [
  { value: 'documentation', label: 'Documentation' },
  { value: 'onboarding',    label: 'Onboarding' },
  { value: 'equipment',     label: 'Equipment' },
  { value: 'training',      label: 'Training' },
  { value: 'other',         label: 'Other' },
];

const ChecklistTemplateManager = () => {
  const { userData } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);

  const [newItem, setNewItem] = useState({ title: '', description: '', category: 'onboarding' });
  const [editItem, setEditItem] = useState({});

  const canManage =
    userData?.role === 'superadmin' || userData?.checklistAccess === true;

  const fetchTemplates = async () => {
    try {
      const q = query(collection(db, 'checklist_templates'), orderBy('order', 'asc'));
      const snap = await getDocs(q);
      setTemplates(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error('Error loading templates:', err);
      toast.error('Failed to load checklist templates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  // Seed default templates (only if collection is empty)
  const seedDefaults = async () => {
    if (templates.length > 0) {
      toast.error('Templates already exist. Clear them first to re-seed.');
      return;
    }
    setSeeding(true);
    const toastId = toast.loading('Seeding default checklist items...');
    try {
      const batch = writeBatch(db);
      for (const item of DEFAULT_TEMPLATES) {
        const ref = doc(collection(db, 'checklist_templates'));
        batch.set(ref, {
          ...item,
          isActive: true,
          createdBy: userData.uid,
          createdByName: userData.name,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      }
      await batch.commit();
      toast.success(`${DEFAULT_TEMPLATES.length} default items added!`, { id: toastId });
      await fetchTemplates();
    } catch (err) {
      console.error('Seed error:', err);
      toast.error('Failed to seed defaults.', { id: toastId });
    } finally {
      setSeeding(false);
    }
  };

  const addTemplate = async () => {
    if (!newItem.title.trim()) {
      toast.error('Title is required.');
      return;
    }
    setSaving(true);
    const toastId = toast.loading('Adding item...');
    try {
      const nextOrder = templates.length > 0
        ? Math.max(...templates.map(t => t.order || 0)) + 1
        : 1;
      await addDoc(collection(db, 'checklist_templates'), {
        title: newItem.title.trim(),
        description: newItem.description.trim(),
        category: newItem.category,
        order: nextOrder,
        isActive: true,
        createdBy: userData.uid,
        createdByName: userData.name,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      toast.success('Item added!', { id: toastId });
      setNewItem({ title: '', description: '', category: 'onboarding' });
      setShowAddForm(false);
      await fetchTemplates();
    } catch (err) {
      console.error('Add error:', err);
      toast.error('Failed to add item.', { id: toastId });
    } finally {
      setSaving(false);
    }
  };

  const saveEdit = async () => {
    if (!editItem.title?.trim()) {
      toast.error('Title is required.');
      return;
    }
    setSaving(true);
    const toastId = toast.loading('Saving...');
    try {
      await updateDoc(doc(db, 'checklist_templates', editingId), {
        title: editItem.title.trim(),
        description: editItem.description?.trim() || '',
        category: editItem.category || 'other',
        updatedAt: serverTimestamp()
      });
      toast.success('Item updated!', { id: toastId });
      setEditingId(null);
      await fetchTemplates();
    } catch (err) {
      console.error('Edit error:', err);
      toast.error('Failed to update item.', { id: toastId });
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (template) => {
    try {
      await updateDoc(doc(db, 'checklist_templates', template.id), {
        isActive: !template.isActive,
        updatedAt: serverTimestamp()
      });
      toast.success(template.isActive ? 'Item disabled.' : 'Item enabled.');
      await fetchTemplates();
    } catch (err) {
      console.error('Toggle error:', err);
      toast.error('Failed to update status.');
    }
  };

  const moveItem = async (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= templates.length) return;

    const batch = writeBatch(db);
    const itemA = templates[index];
    const itemB = templates[newIndex];
    batch.update(doc(db, 'checklist_templates', itemA.id), { order: itemB.order ?? newIndex + 1 });
    batch.update(doc(db, 'checklist_templates', itemB.id), { order: itemA.order ?? index + 1 });

    try {
      await batch.commit();
      await fetchTemplates();
    } catch (err) {
      console.error('Reorder error:', err);
      toast.error('Failed to reorder.');
    }
  };

  const startEdit = (template) => {
    setEditingId(template.id);
    setEditItem({
      title: template.title,
      description: template.description || '',
      category: template.category || 'other'
    });
    setShowAddForm(false);
  };

  const categoryLabel = (value) =>
    CATEGORIES.find(c => c.value === value)?.label || value;

  if (!canManage) {
    return (
      <div className={styles.accessDenied}>
        <FaClipboardList size={56} />
        <h2>Access Denied</h2>
        <p>Only superadmin or users with Checklist Access permission can manage templates.</p>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.pageHeader}>
        <div className={styles.headerLeft}>
          <FaClipboardList size={30} />
          <div>
            <h1>Checklist Template Manager</h1>
            <p>Define the onboarding checklist items that appear for every employee.</p>
          </div>
        </div>
        <div className={styles.headerActions}>
          {templates.length === 0 && (
            <button
              className={`${styles.btn} ${styles.btnSeed}`}
              onClick={seedDefaults}
              disabled={seeding}
            >
              {seeding ? <FaSpinner className={styles.spin} /> : <FaPlus />}
              Load Defaults
            </button>
          )}
          <button
            className={`${styles.btn} ${styles.btnPrimary}`}
            onClick={() => { setShowAddForm(v => !v); setEditingId(null); }}
          >
            {showAddForm ? <><FaTimes /> Cancel</> : <><FaPlus /> Add Item</>}
          </button>
        </div>
      </div>

      {/* Add form */}
      {showAddForm && (
        <div className={styles.addFormCard}>
          <h3>New Checklist Item</h3>
          <div className={styles.formGrid}>
            <div className={styles.formField}>
              <label>Title *</label>
              <input
                type="text"
                placeholder="e.g. Offer Letter Signed Back"
                value={newItem.title}
                onChange={(e) => setNewItem(p => ({ ...p, title: e.target.value }))}
                maxLength={120}
              />
            </div>
            <div className={styles.formField}>
              <label>Category</label>
              <select
                value={newItem.category}
                onChange={(e) => setNewItem(p => ({ ...p, category: e.target.value }))}
              >
                {CATEGORIES.map(c => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>
            <div className={`${styles.formField} ${styles.fullWidth}`}>
              <label>Description <span className={styles.optional}>(optional)</span></label>
              <input
                type="text"
                placeholder="Short description of the requirement"
                value={newItem.description}
                onChange={(e) => setNewItem(p => ({ ...p, description: e.target.value }))}
                maxLength={250}
              />
            </div>
          </div>
          <div className={styles.formActions}>
            <button
              className={`${styles.btn} ${styles.btnPrimary}`}
              onClick={addTemplate}
              disabled={saving || !newItem.title.trim()}
            >
              {saving ? <FaSpinner className={styles.spin} /> : <FaPlus />} Add Item
            </button>
            <button
              className={`${styles.btn} ${styles.btnGhost}`}
              onClick={() => setShowAddForm(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Info note */}
      <div className={styles.infoNote}>
        <FaInfoCircle />
        <span>
          Disabled items are hidden from employee checklists but their past data is preserved.
          Reorder items using the arrows — the order shown here is the order employees see.
        </span>
      </div>

      {/* Template list */}
      {loading ? (
        <div className={styles.loadingState}>
          <FaSpinner className={styles.spin} />
          <p>Loading templates...</p>
        </div>
      ) : templates.length === 0 ? (
        <div className={styles.emptyState}>
          <FaClipboardList size={48} />
          <p>No checklist items yet.</p>
          <p>Click <strong>Load Defaults</strong> to add the standard onboarding items, or <strong>Add Item</strong> to create your own.</p>
        </div>
      ) : (
        <div className={styles.templateList}>
          {templates.map((template, index) => (
            <div
              key={template.id}
              className={`${styles.templateCard} ${!template.isActive ? styles.disabled : ''}`}
            >
              {editingId === template.id ? (
                /* Edit mode */
                <div className={styles.editForm}>
                  <div className={styles.formGrid}>
                    <div className={styles.formField}>
                      <label>Title *</label>
                      <input
                        type="text"
                        value={editItem.title}
                        onChange={(e) => setEditItem(p => ({ ...p, title: e.target.value }))}
                        maxLength={120}
                        autoFocus
                      />
                    </div>
                    <div className={styles.formField}>
                      <label>Category</label>
                      <select
                        value={editItem.category}
                        onChange={(e) => setEditItem(p => ({ ...p, category: e.target.value }))}
                      >
                        {CATEGORIES.map(c => (
                          <option key={c.value} value={c.value}>{c.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className={`${styles.formField} ${styles.fullWidth}`}>
                      <label>Description</label>
                      <input
                        type="text"
                        value={editItem.description}
                        onChange={(e) => setEditItem(p => ({ ...p, description: e.target.value }))}
                        maxLength={250}
                      />
                    </div>
                  </div>
                  <div className={styles.formActions}>
                    <button
                      className={`${styles.btn} ${styles.btnPrimary}`}
                      onClick={saveEdit}
                      disabled={saving}
                    >
                      {saving ? <FaSpinner className={styles.spin} /> : <FaSave />} Save
                    </button>
                    <button
                      className={`${styles.btn} ${styles.btnGhost}`}
                      onClick={() => setEditingId(null)}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                /* View mode */
                <div className={styles.cardContent}>
                  <div className={styles.cardOrder}>{index + 1}</div>
                  <div className={styles.cardInfo}>
                    <div className={styles.cardTitle}>
                      {template.title}
                      {!template.isActive && <span className={styles.disabledBadge}>Disabled</span>}
                    </div>
                    {template.description && (
                      <div className={styles.cardDesc}>{template.description}</div>
                    )}
                    <div className={styles.cardMeta}>
                      <span className={`${styles.categoryTag} ${styles[`cat_${template.category}`]}`}>
                        {categoryLabel(template.category)}
                      </span>
                      <span className={styles.createdBy}>
                        Added by {template.createdByName || 'Unknown'}
                      </span>
                    </div>
                  </div>
                  <div className={styles.cardActions}>
                    <button
                      className={`${styles.iconBtn}`}
                      onClick={() => moveItem(index, -1)}
                      disabled={index === 0}
                      title="Move up"
                    >
                      <FaArrowUp />
                    </button>
                    <button
                      className={`${styles.iconBtn}`}
                      onClick={() => moveItem(index, 1)}
                      disabled={index === templates.length - 1}
                      title="Move down"
                    >
                      <FaArrowDown />
                    </button>
                    <button
                      className={`${styles.iconBtn} ${styles.editBtn}`}
                      onClick={() => startEdit(template)}
                      title="Edit"
                    >
                      <FaEdit />
                    </button>
                    <button
                      className={`${styles.iconBtn} ${template.isActive ? styles.toggleOnBtn : styles.toggleOffBtn}`}
                      onClick={() => toggleActive(template)}
                      title={template.isActive ? 'Disable this item' : 'Enable this item'}
                    >
                      {template.isActive ? <FaToggleOn /> : <FaToggleOff />}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ChecklistTemplateManager;
